import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CheckIcon, UserGroupIcon, BuildingStorefrontIcon, ArrowPathIcon, CalendarIcon, CloudArrowUpIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { redirectToStripeCheckout } from '../lib/stripeCheckout';
import { useSession } from '@supabase/auth-helpers-react';
import toast from 'react-hot-toast';
import { trackEvent } from '../lib/analytics';
import { track } from '../lib/track';
import { useUserTypes } from '../hooks/useUserTypes';
import { useSubscription } from '../hooks/useSubscription';

type BillingCycle = 'monthly' | 'annual';

const FAMILY_SYNC_MONTHLY_PRICE = 6;
const FAMILY_SYNC_ANNUAL_PRICE = 60;

const freeFeatures = [
  'Search local, verified camps',
  'Filter by age, activity, cost, format',
  'Save camps you\'re considering',
  'Build kids\' schedules in one place',
  'View your Family Camp Calendar',
  'Export or print weekly views',
];

const syncFeatures = [
  {
    icon: ArrowPathIcon,
    text: 'Sync to Google Calendar or iCal',
  },
  {
    icon: CalendarIcon,
    text: 'Share a live calendar with co-parents & caregivers',
  },
  {
    icon: CloudArrowUpIcon,
    text: 'Auto-updates across all devices',
  },
];

export default function PricingParentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual');

  // Get user types from the user_types table
  const { userTypes, isParent } = useUserTypes();
  
  // Check if user already has Family Sync or lifetime access
  // Only check subscription if user is logged in
  const { hasFamilySync, isLoading: checkingSubscription } = useSubscription();
  
  // If user is not logged in, don't wait for subscription check
  const isChecking = session?.user ? checkingSubscription : false;

  const cancelledToastShownRef = useRef(false);

  // Redirect camp-owner-only users to owners pricing
  useEffect(() => {
    if (session?.user && userTypes.includes('camp-owner') && !isParent) {
      navigate('/pricing/owners', { replace: true });
      return;
    }
  }, [session, userTypes, isParent, navigate]);

  // Detect payment cancellation from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cancelled = params.get('cancelled');

    if (cancelled === 'true') {
      if (!cancelledToastShownRef.current) {
        cancelledToastShownRef.current = true;
        toast.error('Payment cancelled. You can try again when ready.', {
          duration: 5000,
          icon: '❌',
        });
        track('payment_cancelled', {
          persona: 'parent',
          page: 'pricing_parents',
          plan: 'family-sync',
        });
      }
      navigate('/pricing/parents', { replace: true });
    } else {
      cancelledToastShownRef.current = false;
    }
  }, [location.search, navigate]);

  // Remove excessive bottom padding from main element
  useEffect(() => {
    const mainElement = document.querySelector('main[role="main"]') as HTMLElement;
    if (mainElement) {
      // Set inline style to override py-12's padding-bottom
      mainElement.style.paddingBottom = '0';
      return () => {
        // Restore by removing inline style (Tailwind class will take over)
        mainElement.style.paddingBottom = '';
      };
    }
  }, []);

  const handleFamilySyncUpgrade = async () => {
    // Prevent duplicate purchases
    if (hasFamilySync) {
      toast.success('You already have Family Sync! Redirecting to your schedule...', {
        duration: 3000,
      });
      navigate('/kids-schedule');
      return;
    }

    // Track upgrade button click (dataLayer + PostHog for GTM/Meta)
    trackEvent('pricing_upgrade_clicked', {
      page: 'pricing_parents',
      plan: 'family-sync',
      billing_cycle: billingCycle,
      is_authenticated: !!session
    });
    track('pricing_upgrade_clicked', {
      persona: 'parent',
      page: 'pricing_parents',
      plan: 'family-sync',
      billing_cycle: billingCycle,
      is_authenticated: !!session,
    });

    // If not logged in, redirect to registration with plan parameter
    if (!session) {
      trackEvent('pricing_checkout_blocked_no_auth', {
        plan: 'family-sync',
        billing_cycle: billingCycle,
        redirect_to: 'register'
      });
      track('pricing_checkout_blocked_no_auth', {
        persona: 'parent',
        page: 'pricing_parents',
        plan: 'family-sync',
        redirect_to: 'register',
      });
      navigate('/register?type=parent&plan=family-sync&billing=' + billingCycle);
      return;
    }

    // Get price ID based on billing cycle
    const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
    const priceId = billingCycle === 'monthly'
      ? (isDev 
          ? import.meta.env.VITE_STRIPE_FAMILY_SYNC_MONTHLY_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_FAMILY_SYNC_MONTHLY_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_FAMILY_SYNC_MONTHLY_PRICE_ID || '')
      : (isDev
          ? import.meta.env.VITE_STRIPE_FAMILY_SYNC_ANNUAL_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_FAMILY_SYNC_ANNUAL_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_FAMILY_SYNC_ANNUAL_PRICE_ID || '');

    if (!priceId) {
      toast.error('Payment system not configured. Please contact support.', {
        duration: 5000,
      });
      return;
    }

    try {
      trackEvent('pricing_checkout_initiated', {
        page: 'pricing_parents',
        plan: 'family-sync',
        billing_cycle: billingCycle,
        is_authenticated: true
      });
      track('pricing_checkout_initiated', {
        persona: 'parent',
        page: 'pricing_parents',
        plan: 'family-sync',
        billing_cycle: billingCycle,
        is_authenticated: true,
      });

      await redirectToStripeCheckout({
        priceId,
        planName: 'family-sync',
        userEmail: session?.user?.email,
      });
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      trackEvent('pricing_checkout_error', {
        page: 'pricing_parents',
        plan: 'family-sync',
        billing_cycle: billingCycle,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
      track('pricing_checkout_error', {
        persona: 'parent',
        page: 'pricing_parents',
        plan: 'family-sync',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      toast.error(
        error instanceof Error ? error.message : 'Failed to start checkout. Please try again.',
        { duration: 5000 }
      );
    }
  };

  const isLoggedInParent = session?.user && isParent;

  return (
    <div className="page-container" style={{ minHeight: 'auto', paddingBottom: 0 }}>
      <SEO
        title="Parent Pricing Plans | CampWhiz"
        description="Simple pricing for families. Sync and share your kids' camp schedules with family members."
        keywords="parent pricing, family sync, camp schedule, calendar sync"
        url="/pricing/parents"
      />
      
      <div className="page-content">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
            Simple pricing for families
          </h1>
          <p className="text-lg text-gray-600">
            {isLoggedInParent 
              ? 'Sync and share your kids\' schedule with family or other parents'
              : 'Start FREE. Sync and share when you\'re ready.'
            }
          </p>
        </div>

        {/* Audience Selector - Hidden for logged-in parents */}
        {!isLoggedInParent && (
          <div className="flex justify-center mb-12">
            <div className="inline-flex rounded-xl border-2 border-gray-200 bg-white p-1">
              <button
                onClick={() => {
                  trackEvent('pricing_audience_switched', {
                    from: 'parents',
                    to: 'owners',
                    page: 'pricing_parents'
                  });
                  navigate('/pricing/owners');
                }}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all text-gray-600 hover:text-gray-900"
              >
                <BuildingStorefrontIcon className="h-5 w-5" aria-hidden="true" />
                Camp Owners
              </button>
              <button
                onClick={() => {
                  trackEvent('pricing_audience_switched', {
                    from: 'owners',
                    to: 'parents',
                    page: 'pricing_parents'
                  });
                  navigate('/pricing/parents');
                }}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-primary text-white shadow-sm"
              >
                <UserGroupIcon className="h-5 w-5" aria-hidden="true" />
                Parents
              </button>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-6">
          {/* Free Plan Card - White with gray border */}
          <div className="relative rounded-2xl shadow-lg border border-gray-200 bg-white/95 backdrop-blur-sm">
            <div className="p-6">
              {/* Badge */}
              <div className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mb-4">
                FREE
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Keep it simple while you're planning.
              </h3>

              {/* Features List */}
              <ul className="space-y-3 mb-6">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-base text-gray-700">
                    <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {isLoggedInParent ? (
                <Link
                  to="/kids-schedule"
                  onClick={() => {
                    trackEvent('pricing_cta_clicked', {
                      page: 'pricing_parents',
                      cta_location: 'free_plan_card',
                      cta_text: 'Go to Schedule',
                      destination: 'kids-schedule',
                      is_authenticated: true
                    });
                  }}
                  className="w-full btn btn-primary text-center"
                >
                  Go to Schedule
                </Link>
              ) : (
                <Link
                  to="/kids-schedule"
                  onClick={() => {
                    trackEvent('pricing_cta_clicked', {
                      page: 'pricing_parents',
                      cta_location: 'free_plan_card',
                      cta_text: 'Start FREE',
                      destination: 'kids-schedule',
                      is_authenticated: false
                    });
                  }}
                  className="w-full btn btn-primary text-center"
                >
                  Start FREE
                </Link>
              )}
            </div>
          </div>

          {/* Family Sync Plan Card - Purple background */}
          <div className="relative rounded-2xl shadow-lg border-2 border-primary bg-primary/95 backdrop-blur-sm">
            <div className="p-6">
              {/* Badge */}
              <div className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                Family Sync
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-4">
                For families who want everything in one calendar.
              </h3>

              {/* Billing Cycle Toggle */}
              <div className="flex justify-start mb-4">
                <div className="inline-flex rounded-lg border-2 border-white/30 bg-white/10 p-1">
                  <button
                    onClick={() => {
                      trackEvent('pricing_billing_cycle_changed', {
                        page: 'pricing_parents',
                        plan: 'family-sync',
                        from: billingCycle,
                        to: 'monthly'
                      });
                      setBillingCycle('monthly');
                    }}
                    className={`
                      px-4 py-1 rounded-md text-sm font-medium transition-all
                      ${billingCycle === 'monthly'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-white hover:bg-white/20'
                      }
                    `}
                  >
                    1 month
                  </button>
                  <button
                    onClick={() => {
                      trackEvent('pricing_billing_cycle_changed', {
                        page: 'pricing_parents',
                        plan: 'family-sync',
                        from: billingCycle,
                        to: 'annual'
                      });
                      setBillingCycle('annual');
                    }}
                    className={`
                      px-4 py-1 rounded-md text-sm font-medium transition-all
                      ${billingCycle === 'annual'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-white hover:bg-white/20'
                      }
                    `}
                  >
                    12 months
                  </button>
                </div>
              </div>

              {/* Price Display */}
              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-white">
                    ${billingCycle === 'monthly' ? FAMILY_SYNC_MONTHLY_PRICE : FAMILY_SYNC_ANNUAL_PRICE}
                  </span>
                  <span className="text-xl text-white/90">
                    /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
                {billingCycle === 'annual' && (
                  <p className="text-sm text-white/90 mt-1">
                    Save 17% (2 months FREE)
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {syncFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-base text-white">
                    <feature.icon className="h-5 w-5 text-white flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {hasFamilySync ? (
                <Link
                  to="/kids-schedule"
                  className="w-full btn btn-secondary hover:bg-gray-100 hover:text-primary text-center"
                >
                  You have Family Sync! Go to Schedule
                </Link>
              ) : (
                <button
                  onClick={handleFamilySyncUpgrade}
                  disabled={isChecking}
                  className="w-full btn btn-secondary hover:bg-gray-100 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isChecking ? 'Checking...' : 'Upgrade & Sync'}
                </button>
              )}

              {/* Secure Checkout */}
              <div className="flex items-center justify-center gap-2 text-xs text-white/80 mt-4">
                <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
                <span>
                  Secure checkout by{' '}
                  <a 
                    href="https://docs.stripe.com/security" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white underline hover:text-white/80"
                  >
                    Stripe
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Text */}
        <div className="text-center mt-4">
          <p className="text-gray-600">
            One plan covers your whole family. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
